import { FirebaseError } from 'firebase/app'

import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  signInWithEmailLink,
  applyActionCode,
  isSignInWithEmailLink,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
} from 'firebase/auth'

import { useFirebaseAuth, useCurrentUser } from 'vuefire'

export default function () {
  const auth = useFirebaseAuth()!
  const router = useRouter()
  const runtimeConfig = useRuntimeConfig()

  const currentUser = useCurrentUser()
  const isSignedIn = currentUser.value !== null

  const getCustomClaims = async () => {
    const token = await currentUser.value?.getIdTokenResult()
    if (token) {
      return token.claims
    } else {
      return null
    }
  }

  const isAdmin = async () => {
    const claims = await getCustomClaims()
    if (claims) {
      return claims.role === 'admin'
    }
    return false
  }

  const registerUser = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const userCreds = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      )
      if (userCreds) {
        return { success: true }
      }
    } catch (error) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            return {
              success: false,
              error: 'An account with this email already exists',
            }
          case 'auth/invalid-email':
            return { success: false, error: 'Invalid email address' }
          case 'auth/weak-password':
            return {
              success: false,
              error: 'Password is too weak. Please use at least 6 characters',
            }
          case 'auth/operation-not-allowed':
            return {
              success: false,
              error: 'Email/password accounts are not enabled',
            }
          case 'auth/network-request-failed':
            return {
              success: false,
              error: 'Network error. Please check your internet connection',
            }
          default:
            return {
              success: false,
              error: 'Failed to create account. Please try again',
            }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
    return { success: false, error: 'Registration failed' }
  }

  const signInWithGoogle = async (
    successRedirect: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const googleAuthProvider = new GoogleAuthProvider()

    try {
      const result = await signInWithPopup(auth, googleAuthProvider)
      console.log('result', result)
      router.push(successRedirect)
      return { success: true }
    } catch (error) {
      console.error('error', error)
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            return { success: false, error: 'Sign-in was cancelled' }
          case 'auth/popup-blocked':
            return {
              success: false,
              error: 'Pop-up was blocked by your browser',
            }
          case 'auth/account-exists-with-different-credential':
            return {
              success: false,
              error:
                'An account with this email already exists with a different sign-in method',
            }
          default:
            return { success: false, error: 'Failed to sign in with Google' }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const signInWithApple = async (
    successRedirect: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const appleProvider = new OAuthProvider('apple.com')

    try {
      const result = await signInWithPopup(auth, appleProvider)
      console.log('result', result)
      router.push(successRedirect)
      return { success: true }
    } catch (error) {
      console.error('error', error)
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            return { success: false, error: 'Sign-in was cancelled' }
          case 'auth/popup-blocked':
            return {
              success: false,
              error: 'Pop-up was blocked by your browser',
            }
          case 'auth/account-exists-with-different-credential':
            return {
              success: false,
              error:
                'An account with this email already exists with a different sign-in method',
            }
          default:
            return { success: false, error: 'Failed to sign in with Apple' }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const signInUser = async (
    email: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const userCreds = await signInWithEmailAndPassword(auth, email, password)
      if (userCreds) {
        return { success: true }
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/user-not-found':
            return {
              success: false,
              error: 'No account found with this email address',
            }
          case 'auth/wrong-password':
            return { success: false, error: 'Incorrect password' }
          case 'auth/invalid-email':
            return { success: false, error: 'Invalid email address' }
          case 'auth/user-disabled':
            return { success: false, error: 'This account has been disabled' }
          case 'auth/too-many-requests':
            return {
              success: false,
              error: 'Too many failed sign-in attempts. Please try again later',
            }
          case 'auth/network-request-failed':
            return {
              success: false,
              error: 'Network error. Please check your internet connection',
            }
          default:
            return {
              success: false,
              error: 'Failed to sign in. Please try again',
            }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
    return { success: false, error: 'Sign in failed' }
  }

  const signOutUser = async (): Promise<boolean> => {
    try {
      await auth.signOut()
      navigateTo('/signin')
      return true
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Sign out error:', error.message)
      }
      return false
    }
  }

  const verifyUser = async (email: string, link: string) => {
    //catch auth/invalid-action-code
    console.log('verifying user with link: ', link)

    console.log(
      'is this link a signin link? ',
      isSignInWithEmailLink(auth, link),
    )

    try {
      //TODO: we need to know the email address of the user here to sign in with email link
      const userCreds = await signInWithEmailLink(auth, email, link)
      if (userCreds) {
        return userCreds
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/invalid-action-code') {
          console.log(
            'Code is invalid. Delete account links can only be used once.',
          )
        }
      }
    }
  }

  const signInWithToken = async (token: string): Promise<boolean> => {
    try {
      console.log('signing in with token: ', token)
      const userCreds = await signInWithCustomToken(auth, token)

      applyActionCode(auth, token)
      if (userCreds) {
        return true
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
      }
      return false
    }
    return false
  }

  const sendPasswordReset = async (
    email: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await sendPasswordResetEmail(auth, email)
      return { success: true }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/user-not-found':
            return {
              success: false,
              error: 'No account found with this email address',
            }
          case 'auth/invalid-email':
            return { success: false, error: 'Invalid email address' }
          case 'auth/too-many-requests':
            return {
              success: false,
              error: 'Too many requests. Please try again later',
            }
          default:
            return {
              success: false,
              error: 'Failed to send password reset email',
            }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const resendVerificationEmail = async (): Promise<{
    success: boolean
    error?: string
  }> => {
    try {
      if (!currentUser.value) {
        return { success: false, error: 'No user is currently signed in' }
      }

      await sendEmailVerification(currentUser.value, {
        url: runtimeConfig.public.galleryUrl,
      })
      return { success: true }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/too-many-requests':
            return {
              success: false,
              error: 'Too many requests. Please wait before trying again',
            }
          case 'auth/user-not-found':
            return { success: false, error: 'User account not found' }
          default:
            return {
              success: false,
              error: 'Failed to send verification email',
            }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  const resetPassword = async (
    newPassword: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!currentUser.value) {
        return { success: false, error: 'No user is currently signed in' }
      }

      await updatePassword(currentUser.value, newPassword)
      return { success: true }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/weak-password':
            return {
              success: false,
              error: 'Password is too weak. Please use at least 6 characters',
            }
          case 'auth/requires-recent-login':
            return {
              success: false,
              error: 'Please sign in again and then try changing your password',
            }
          default:
            return { success: false, error: 'Failed to update password' }
        }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }

  return {
    isAdmin,
    getCustomClaims,
    registerUser,
    signInUser,
    signOutUser,
    isSignedIn,
    verifyUser,
    signInWithToken,
    signInWithGoogle,
    signInWithApple,
    sendPasswordReset,
    resendVerificationEmail,
    resetPassword,
  }
}
